import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { key: 2 }
const _hoisted_3 = { key: 3 }

import { useRouter } from 'vue-router';
import { watch, ref, onMounted, computed } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import { InsuranceVehicleDetail, ISitecoreFormGenericInput, OwnedAssistanceProduct } from '@/interfaces';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { getFormInput } from '@/utils/form-input-helper';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import { convertArrayToObject } from '@/utils/data-transforms';
import useFormInputData from '@/composables/useFormInputData';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import vehicleService from '@/services/store/vehicle.service';
import { RoadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { getRsaPolicyStatusDetails } from '@/utils/self-service-rules/RSAPolicyStatusRules';
import { useAccountStore, useAnalyticsStore } from '@/store';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { FormEvents } from '@/enums/form-events.enum';
import { OriginatingSystem } from '@/constants/originating-system.const';

enum ModalState {
  NEW_REGO = 'newRego',
  COMPLETE = 'complete',
  CMO_ERROR = 'cmoError',
  EXPIRED_ERROR = 'expiredError',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RsaChangeRegistrationModal',
  props: {
    content: {},
    policy: {}
  },
  setup(__props: any) {

const props = __props;

const changeRegistrationTitle = getFormInput(props.content, 'changeRegistrationTitle');
const updateBtn = getFormInput(props.content, 'updateBtn');
const closeBtn = getFormInput(props.content, 'closeBtn');
const doneBtn = getFormInput(props.content, 'doneBtn');
const cmoError = getFormInput(props.content, 'cmoError');
const expiredError = getFormInput(props.content, 'expiredError');
const validationError = getFormInput(props.content, 'validationError');

const formInputs = convertArrayToObject(props.content);
const { formInputData } = useFormInputData(formInputs);

const router = useRouter();
const accountStore = useAccountStore();
const analyticsStore = useAnalyticsStore();

const currentModalState = ref<ModalState>(ModalState.NEW_REGO);
const showModal = ref(false);
const showValidationError = ref(false);
const isLoading = ref(false);
const newRegoDetails = ref<{
  registrationNumber?: string;
}>({});

const completedText = computed(() => {
  const richText = getFormInput(props.content, 'success')?.label?.value as string;
  return replaceBookmark('newRego', richText, newRegoDetails.value.registrationNumber ?? '');
});

watch(
  () => router.currentRoute.value.hash,
  (newVal) => {
    // Start change rego modal
    if (newVal.includes('#changeRego')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      router.currentRoute.value.hash = '';
      showModal.value = true;

      //-------------------------------
      // Push analytics START
      //-------------------------------
      analyticsStore.pushAnalytics({
        event: FormEvents.START,
        form: {
          name: 'Update vehicle registration',
          stepName: 'FormStarted',
          productName: props.policy?.tierDescription,
        },
      });

      if (currentModalState.value != ModalState.NEW_REGO) {
        pushJeopardy('Invalid policy status');
      }
      //-------------------------------
      // Push analytics END
      //-------------------------------
    }
  },
  {
    deep: true,
  }
);

watch(showModal, (newVal, oldVal) => {
  if (!newVal && oldVal) resetModal();
});

const resetModal = () => {
  showModal.value = false;
  // Wait 1s for modal close animation to avoid users seeing flash
  setTimeout(() => {
    showValidationError.value = false;
    isLoading.value = false;
    newRegoDetails.value = {};
    if (!props.policy) return (currentModalState.value = ModalState.CMO_ERROR);
    const status = getRsaPolicyStatusDetails(props.policy!);
    if (status.isCMO || status.isFordNCO) currentModalState.value = ModalState.CMO_ERROR;
    else if (status.isExpiredOrLapse) currentModalState.value = ModalState.EXPIRED_ERROR;
    else currentModalState.value = ModalState.NEW_REGO;
  }, 1000);
};

onMounted(resetModal);

/**
 * Search registration number,
 * Update if matches
 */
const findRego = async () => {
  isLoading.value = true;
  try {
    newRegoDetails.value.registrationNumber = newRegoDetails.value.registrationNumber
      ?.toUpperCase()
      .replaceAll(' ', '');
    if (newRegoDetails.value.registrationNumber == props.policy?.vehicleDetail.registrationNumber?.toUpperCase())
      throw new Error('Same registration number');
    const vehicleDataFromRego = await vehicleService.regoLookup(newRegoDetails.value.registrationNumber!, OriginatingSystem.Roadside);
    showValidationError.value = !vehicleDataFromRego;
    vehicleDataFromRego && (await changeRego(vehicleDataFromRego));
  } catch {
    currentModalState.value = ModalState.CMO_ERROR;
    pushJeopardy('Unable to retrieve vehicle details');
  }
  isLoading.value = false;
};

/**
 * Validate and update rego
 *
 * @param vehicleDetail
 */
const changeRego = async (vehicleDetail: InsuranceVehicleDetail) => {
  // Validate rego
  const sameYear = vehicleDetail.manufacturingYear == props.policy?.vehicleDetail.manufacturingYear;
  const sameMake =
    vehicleDetail.make?.description
      ?.toLowerCase()
      .includes(props.policy?.vehicleDetail?.make?.description?.toLowerCase() ?? '') ||
    props.policy?.vehicleDetail?.make?.description
      ?.toLowerCase()
      .includes(vehicleDetail.make?.description?.toLowerCase() ?? '');
  const sameModel =
    vehicleDetail.model?.description
      ?.toLowerCase()
      .includes(props.policy?.vehicleDetail?.model?.description?.toLowerCase() ?? '') ||
    props.policy?.vehicleDetail?.model?.description
      ?.toLowerCase()
      .includes(vehicleDetail.model?.description?.toLowerCase());
  if (!sameYear || !sameMake || !sameModel) {
    pushJeopardy('Vehicle details mismatch');
    showValidationError.value = true;
    return;
  }

  // Update rego
  const policyId = router.currentRoute.value.query.policyId;
  await RoadsideAssistanceService.changeRego(newRegoDetails.value.registrationNumber!, policyId as string);
  currentModalState.value = ModalState.COMPLETE;

  // Update portfolio
  const product = accountStore.identityDetails?.portfolio?.assistanceProducts?.find(
    (product) => product.policyId == policyId && product.vehicleDetail
  );
  if (product?.policyId == policyId && product?.vehicleDetail) {
    product.vehicleDetail.registrationNumber = newRegoDetails.value.registrationNumber!;
  }

  analyticsStore.pushAnalytics({
    event: FormEvents.COMPLETE,
    form: {
      name: 'Update vehicle registration',
      stepName: 'FormCompleted',
      productName: props.policy?.tierDescription,
    },
  });
};

const pushJeopardy = (error: string) => {
  analyticsStore.pushAnalytics({
    event: FormEvents.JEOPARDY,
    form: {
      name: 'Update vehicle registration',
      formErrorType: error,
      productName: props.policy?.tierDescription,
    },
  });
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createBlock(ModalDialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
    size: "md",
    showXCloseButton: true,
    closeOnEsc: false,
    isLoading: isLoading.value
  }, {
    default: _withCtx(() => [
      (ModalState.NEW_REGO == currentModalState.value)
        ? (_openBlock(), _createBlock(_component_FormKit, {
            key: 0,
            modelValue: newRegoDetails.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newRegoDetails).value = $event)),
            type: "form",
            onSubmit: findRego
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(RichText), {
                field: _unref(changeRegistrationTitle).label
              }, null, 8, ["field"]),
              _createVNode(RACQFormKit, {
                bindings: _unref(formInputData)('registrationNumber')
              }, null, 8, ["bindings"]),
              (showValidationError.value)
                ? (_openBlock(), _createBlock(InformationBox, {
                    key: 0,
                    type: _unref(InformationBoxTypes).ERROR,
                    title: _unref(validationError)?.title?.value,
                    description: _unref(validationError)?.content?.value
                  }, null, 8, ["type", "title", "description"]))
                : _createCommentVNode("", true),
              _createVNode(_component_FormKit, {
                type: "submit",
                outerClass: showValidationError.value ? 'mt-8' : 'mt-2',
                inputClass: "btn-md btn w-full btn-primary",
                label: _unref(updateBtn)?.label?.value,
                disabled: isLoading.value
              }, null, 8, ["outerClass", "label", "disabled"])
            ]),
            _: 1
          }, 8, ["modelValue"]))
        : (ModalState.COMPLETE == currentModalState.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_unref(RichText), {
                field: { value: completedText.value }
              }, null, 8, ["field"]),
              _createElementVNode("button", {
                class: "mt-2 btn w-full btn-md btn-primary",
                onClick: resetModal
              }, _toDisplayString(_unref(doneBtn)?.label?.value), 1)
            ]))
          : (ModalState.CMO_ERROR == currentModalState.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_unref(RichText), {
                  field: _unref(cmoError)?.label
                }, null, 8, ["field"]),
                _createElementVNode("button", {
                  class: "mt-2 btn w-full btn-md btn-secondary",
                  onClick: resetModal
                }, _toDisplayString(_unref(closeBtn)?.label?.value), 1)
              ]))
            : (ModalState.EXPIRED_ERROR == currentModalState.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(_unref(RichText), {
                    field: _unref(expiredError)?.label
                  }, null, 8, ["field"]),
                  _createElementVNode("button", {
                    class: "mt-2 btn w-full btn-md btn-secondary",
                    onClick: resetModal
                  }, _toDisplayString(_unref(closeBtn)?.label?.value), 1)
                ]))
              : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue", "isLoading"]))
}
}

})